/**
 * Submittal API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  AddDistributionRequest,
  AddRevisionRequest,
  AssignBallInCourtRequest,
  BulkAssignRequest,
  CreateSubmittalRequest,
  ReviewSubmittalRequest,
  SetReviewersRequest,
  Submittal,
  SubmitForReviewRequest,
  SubmittalListParams,
  SubmittalListResponse,
  SubmittalStats,
  UpdateSubmittalRequest,
} from '../types/submittal';

/**
 * Submittal API Endpoints
 */
export const submittalApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // SUBMITTAL CRUD
    // ============================================================================

    // Get all submittals for a project
    getSubmittals: builder.query<
      ApiResponse<SubmittalListResponse>,
      { projectId: string; params?: SubmittalListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/submittals`,
        params,
      }),
      providesTags: ['Submittal'],
    }),

    // Get single submittal by ID
    getSubmittal: builder.query<ApiResponse<Submittal>, { projectId: string; submittalId: string }>(
      {
        query: ({ projectId, submittalId }) => ({
          url: `/projects/${projectId}/submittals/${submittalId}`,
        }),
        providesTags: (_result, _error, { submittalId }) => [
          { type: 'Submittal', id: submittalId },
        ],
      }
    ),

    // Create a new submittal
    createSubmittal: builder.mutation<
      ApiResponse<Submittal>,
      { projectId: string; data: CreateSubmittalRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/submittals`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Submittal'],
    }),

    // Update a submittal
    updateSubmittal: builder.mutation<
      ApiResponse<Submittal>,
      { projectId: string; submittalId: string; data: UpdateSubmittalRequest }
    >({
      query: ({ projectId, submittalId, data }) => ({
        url: `/projects/${projectId}/submittals/${submittalId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { submittalId }) => [
        'Submittal',
        { type: 'Submittal', id: submittalId },
      ],
    }),

    // Delete a submittal (soft delete)
    deleteSubmittal: builder.mutation<
      ApiResponse<void>,
      { projectId: string; submittalId: string }
    >({
      query: ({ projectId, submittalId }) => ({
        url: `/projects/${projectId}/submittals/${submittalId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Submittal'],
    }),

    // ============================================================================
    // STATISTICS
    // ============================================================================

    // Get submittal stats for a project
    getSubmittalStats: builder.query<ApiResponse<SubmittalStats>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/submittals/stats`,
      }),
      providesTags: ['Submittal'],
    }),

    // ============================================================================
    // WORKFLOW
    // ============================================================================

    // Submit for review
    submitForReview: builder.mutation<
      ApiResponse<Submittal>,
      { projectId: string; submittalId: string; data?: SubmitForReviewRequest }
    >({
      query: ({ projectId, submittalId, data }) => ({
        url: `/projects/${projectId}/submittals/${submittalId}/submit`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { submittalId }) => [
        'Submittal',
        { type: 'Submittal', id: submittalId },
      ],
    }),

    // Submit a review response
    submitReview: builder.mutation<
      ApiResponse<Submittal>,
      { projectId: string; submittalId: string; data: ReviewSubmittalRequest }
    >({
      query: ({ projectId, submittalId, data }) => ({
        url: `/projects/${projectId}/submittals/${submittalId}/review`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { submittalId }) => [
        'Submittal',
        { type: 'Submittal', id: submittalId },
      ],
    }),

    // Set / replace review chain
    setReviewers: builder.mutation<
      ApiResponse<Submittal>,
      { projectId: string; submittalId: string; data: SetReviewersRequest }
    >({
      query: ({ projectId, submittalId, data }) => ({
        url: `/projects/${projectId}/submittals/${submittalId}/reviewers`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { submittalId }) => [
        'Submittal',
        { type: 'Submittal', id: submittalId },
      ],
    }),

    // Add a new revision
    addRevision: builder.mutation<
      ApiResponse<Submittal>,
      { projectId: string; submittalId: string; data: AddRevisionRequest }
    >({
      query: ({ projectId, submittalId, data }) => ({
        url: `/projects/${projectId}/submittals/${submittalId}/revisions`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { submittalId }) => [
        'Submittal',
        { type: 'Submittal', id: submittalId },
      ],
    }),

    // Change ball-in-court
    assignBallInCourt: builder.mutation<
      ApiResponse<Submittal>,
      { projectId: string; submittalId: string; data: AssignBallInCourtRequest }
    >({
      query: ({ projectId, submittalId, data }) => ({
        url: `/projects/${projectId}/submittals/${submittalId}/assign`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { submittalId }) => [
        'Submittal',
        { type: 'Submittal', id: submittalId },
      ],
    }),

    // Add to distribution list
    addDistribution: builder.mutation<
      ApiResponse<Submittal>,
      { projectId: string; submittalId: string; data: AddDistributionRequest }
    >({
      query: ({ projectId, submittalId, data }) => ({
        url: `/projects/${projectId}/submittals/${submittalId}/distribution`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { submittalId }) => [
        'Submittal',
        { type: 'Submittal', id: submittalId },
      ],
    }),

    // Bulk reassign ball-in-court
    bulkAssignSubmittals: builder.mutation<
      ApiResponse<{ updated: number; failed: number }>,
      { projectId: string; data: BulkAssignRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/submittals/bulk-assign`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Submittal'],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // CRUD
  useGetSubmittalsQuery,
  useGetSubmittalQuery,
  useCreateSubmittalMutation,
  useUpdateSubmittalMutation,
  useDeleteSubmittalMutation,
  // Stats
  useGetSubmittalStatsQuery,
  // Workflow
  useSubmitForReviewMutation,
  useSubmitReviewMutation,
  useSetReviewersMutation,
  useAddRevisionMutation,
  useAssignBallInCourtMutation,
  useAddDistributionMutation,
  useBulkAssignSubmittalsMutation,
} = submittalApi;

/**
 * Re-export types for convenience
 */
export type { SubmittalListParams } from '../types/submittal';
